import { Component, DestroyRef, inject, signal, ViewChild, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { LearningUnitJsonModel, Preview } from '../learning-unit.json.model';
import { StateFilterCBComponent } from '../../utils/component/state-filter-combobox/state-cb.component';
import { MatTabsModule } from '@angular/material/tabs';
import {
  FederalStatesComboboxComponent
} from '../../utils/component/federal-states-combobox/federal-states-combobox.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetJsonModel } from '../../asset/assets.json.model';
import { MediaType } from '../../core/types';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { LearningSectionJsonModel } from '../../learning-section/learning-section.json.model';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { environment } from '../../../environments/environment';
import { CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Store } from '@ngxs/store';
import { LearningSectionState } from '../../learning-section/ngxs/learning-section/learning-section.state';
import { CommonModule } from '@angular/common';
import { FetchOptionsActions } from '../../core/state/fetchOptions.actions';
import { LearningSectionActions } from '../../learning-section/ngxs/learning-section/learning-section.actions';
import { CollectionFilter } from '../../core/jsonApi/BaseCollectionService';
import { MultiComboSearchComponent } from '../../utils/component/multi-combo-search/multi-combo-search.component';
import { ComboBoxInputData } from '../../utils/component/combo-input/combo-input.component';

@Component({
  selector: 'pixl-learning-unit-edit',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatTabsModule,
    MatDividerModule,
    StateFilterCBComponent,
    FederalStatesComboboxComponent,
    CdkDropList,
    DragDropModule,
    CommonModule,
    MultiComboSearchComponent,
  ],
  templateUrl: './learning-unit-edit.component.html',
  styleUrl: './learning-unit-edit.component.scss'
})
export class LearningUnitEditComponent implements OnInit {
  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef);
  private destroyRef = inject(DestroyRef);
  data = inject<LearningUnitJsonModel>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  learningSections$ = this.store.select(LearningSectionState.getLearningSections);
  @ViewChild('sectionSelect') sectionSelect!: MatSelect;

  @ViewChild(StateFilterCBComponent) stateFilter!: StateFilterCBComponent;

  public desktopAudioUrl = signal('');
  public mobileAudioUrl = signal('');
  public imageUrl = signal('');
  public nameError = signal(false);
  public nameErrorText = signal('');
  public consumerError = signal(false);
  public subjectError = signal(false);
  public consumerIsDeleted = signal(false);
  public competencyIsDeleted = signal(false);
  public isNewRecord = signal(true);
  public searchTerm = '';
  public selectedContent: LearningSectionJsonModel | null = null;
  public currentPage = 1;
  public dataList: ComboBoxInputData[] = [];
  private currentOpenedSelect: MatSelect | null = null;

  public comboBoxValues: string[] = ['Wert1', 'Wert2', 'Wert3', 'Wert4', 'Wert5'];
  // Values for input fields that are part of an own component that can't be accessed by the formGroup
  // These are two-way-bindings to other components
  status = this.data.status && this.data.status.length > 0 ? this.data.status : 'Edit';
  learningSections = this.data.learningSections;
  federal_states = this.data.federal_state;
  moduleIds = this.data.moduleIds;
  preview = '';

  formGroup = this.fb.group({
    title: [this.data.title, [Validators.required]],
    subtitle: [this.data.subtitle, []],
    preview: [this.data.preview, []],
    lesson_section: [this.data.lesson_section, []],
    processing_time: [this.data.processing_time, []],
    federal_state: [this.federal_states, []],
    learning_unit_goal: [this.data.learning_unit_goal, []],
    learning_unit_type: [this.data.learning_unit_type, []],
    description: [this.data.description, []],
    status: [this.status, []],
    learningSections: [this.data.learningSections || []],
  });

  ngOnInit(): void {
    this.resetFetch();
  }

  constructor() {
    this.buildURLs();
    this.destroyRef.onDestroy(() => this.resetFetch());
  }

  // The following three fuctions are to make sure the two way binding between this edit and other components work
  statusChanged(newStatus: string) {
    this.status = newStatus;
  }

  learningSectionsChanged(newlearningSections: LearningSectionJsonModel[]) {
    this.learningSections = newlearningSections;
  }

  federalStatesChanged(newFederalStates: string[]) {
    this.federal_states = newFederalStates;
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: false,
      subjectId: ''
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetJsonModel) => {
        if (answer) {
          let imageModel: Preview;

          if (!this.data.preview) {
            imageModel = new Preview();
          }
          else {
            imageModel = Object.assign(new Preview(), this.data.preview);
          }
          imageModel.id = answer.id;
          imageModel.mediaType = mediaType;
          imageModel.media_url = answer.media_url;
          imageModel.name = answer.name;
          this.data.preview = imageModel;
          this.buildURLs();
        }
      });
  }

  receiveData(event: LearningSectionJsonModel[] | null) {
    let count = 0;
    event?.forEach(value => {
      if (!this.formGroup.value.learningSections?.includes(value)) {
        this.formGroup.value.learningSections?.push(value);
      }
    });
    this.formGroup.value.learningSections?.forEach(value => {
      if (!event?.includes(value)) {
        this.formGroup.value.learningSections?.splice(count, 1);
      }
      count++;
    });
  }

  receiveSearchQuery(event: string) {
    this.searchTerm = event;
    this.onSearchChange();
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ? '/' : 'https://zav-stage.westermann.de/';
    if (this.data.preview && this.data.preview.media_url) {
      this.imageUrl.set(baseUrl + this.data.preview.media_url);
    }
  }

  reorderLearningSections(event: CdkDragDrop<LearningUnitJsonModel[]>) {
    const control = this.formGroup.get('learningSections');
    if (control) {
      const current = [...(control.value || [])];
      moveItemInArray(current, event.previousIndex, event.currentIndex);
      control.setValue(current);
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.data.learningSections, event.previousIndex, event.currentIndex);
  }

  public deleteMediaEntry(): void {
    const id = this.data.preview?.id;
    const name = this.data.preview?.name;
    const headerText = 'Bild-Datei löschen';

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: id,
          name: name,
          itemType: 'Datei',
          headerText: headerText,
          deleteFn: () => {
            this.data.preview = null;
            dialogRef.close();
          }
        },
      });
  }

  resetFetch() {
    this.store.dispatch([
      new FetchOptionsActions.UpdateSortFieldName(''),
      new FetchOptionsActions.UpdateFilters([]),
    ]);

    this.store.dispatch(new LearningSectionActions.Fetch(undefined, {
      pageNumber: 1,
      pageSize: -1,
      skipStoreDispatch: true
    }));

    this.searchTerm = '';
  }

  onSearchChange() {
    if (!this.searchTerm) {
      this.resetFetch();
      return;
    }

    const filters: CollectionFilter[] = [];

    filters.push({ fieldName: 'nameLike', value: this.searchTerm });

    this.store.dispatch([
      new FetchOptionsActions.UpdateFilters(filters),
      new FetchOptionsActions.ToDefault(),
    ]);

    this.store.dispatch(new LearningSectionActions.Fetch(undefined, {
      pageNumber: 1,
      pageSize: -1,
      skipStoreDispatch: true
    }));
  }

  onSelectOpened(opened: boolean, openedSelect: MatSelect) {
    if (opened) {
      this.currentPage = 1;
      this.currentOpenedSelect = openedSelect;

      setTimeout(() => {
        const panel = openedSelect.panel?.nativeElement;
        panel?.addEventListener('scroll', this.onScroll.bind(this));
      }, 0);
    }
    else {
      this.currentOpenedSelect = null;
    }
  }

  onScroll(event: Event) {
    if (!this.currentOpenedSelect) return;

    const target = event.target as HTMLElement;

    if (target.scrollTop + target.clientHeight >= target.scrollHeight) {
      this.currentPage += 1;
      this.store.dispatch(new LearningSectionActions.FetchAppend({
        pageNumber: this.currentPage,
        pageSize: 10,
        skipStoreDispatch: true
      }));
    }
  }

  public getImageName(): string {
    if (this.data.preview && this.data.preview.name) {
      return this.data.preview.name;
    }
    return '';
  }

  public onSave(): void {
    if (this.formGroup.valid) {
      const formData = this.formGroup.value;
      formData.preview = this.data.preview;
      formData.status = this.status;
      if (this.data.hasId()) {
        this.dialogRef.close(this.data);
        this.data.update(formData);
      }
      else {
        const learningUnit = {
          ...this.data,
          ...formData,
          learningSections: [...this.data.learningSections, ...(formData.learningSections ? formData.learningSections : [])]
        };
        this.dialogRef.close(learningUnit);
      }
    }
  }

  onCancel(): void {
    this.resetFetch(); // reset any search querys for learning Sections
    this.dialogRef.close();
  }
}
